import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@platform/ui-kit';
import { api, type SpnDiscoveryOpportunity } from '../../../api';
import { HelpPanel, type Help } from './HelpPanel';
import './DiscoverySession.css';
import './Transfer.css';

/**
 * Strategic Portfolio Navigator → Transfer.
 *
 * Sends opportunities the client has settled on to Inventory Registry, and LOCKS them
 * here. Two products editing the same finding independently is how the two copies stop
 * agreeing — and the one people act on is the other one.
 *
 * TWO LISTS, deliberately. Ready is what was ticked inside a discovery, still editable.
 * Transferred is read-only, greyed, carrying the reference and the date. There is no third
 * state and no workflow: the client has already done the deciding in the session screen.
 *
 * NO CROSS-SCHEMA REFERENCE (CLAUDE.md §1.7). SPN records when it went, where, and a plain
 * reference string. Release arrives as a call from the other product, not as a cascade.
 *
 * THE SEND IS A STUB. Inventory Registry does not exist yet, so the reference is minted
 * locally. Everything around it — the lock, the release, the enforcement — is real.
 */

const HELP: Help = {
  title: 'Transfer',
  icon: '🔒',
  purpose:
    'Hand opportunities you have settled on to <b>Opportunity Registry</b>, where they become funded projects with a benefit case. A transferred opportunity is <b>locked here</b> and cannot be edited, challenged, re-run or deleted — the copy people act on is the one over there.',
  doHere: [
    'Tick what you are sending. Everything listed was already ticked inside a discovery, so this is a last look rather than a fresh decision.',
    'Click <b>Transfer</b>. Only what actually goes is locked — a partial failure leaves the rest alone and names what did not make it.',
    'Read the <b>Transferred</b> list for what has gone, its reference and when.',
    'To get one back, delete it in Opportunity Registry. It is released here automatically and becomes editable again.',
  ],
  fits: 'Strategic Portfolio Navigator &rarr; Transfer',
  firstTime:
    'Open a discovery first and tick the opportunities you are carrying. Nothing appears here until something is ticked there.',
  sections: [
    {
      label: 'What locking means',
      items: [
        { term: 'Locked', color: '#55627a', def: 'Read-only here. Edit, challenge, re-run, delete and un-tick are all refused, with a message saying why.' },
        { term: 'Released', color: '#1f6b38', def: 'The other product deleted its copy, so the lock cleared. The opportunity is editable again and the fact that it went and came back is kept.' },
      ],
      note: 'Nothing in this product points at a row in the other one — the reference is a plain string, and release arrives as a call. Products share no foreign keys.',
    },
  ],
};

const compact = (n: number) => {
  if (!Number.isFinite(n) || n === 0) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}m`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
};

/** The worth of an opportunity under the case the client chose, not the medium default. */
function chosenValue(o: SpnDiscoveryOpportunity): number {
  const level = o.chosen?.benefit ?? 'medium';
  const raw = level === 'low' ? o.ebitda_low : level === 'high' ? o.ebitda_high : o.ebitda_impact;
  return Number(raw) || 0;
}

const METRICS = [
  { key: 'benefit', label: 'Benefit' },
  { key: 'roi', label: 'ROI' },
  { key: 'risk', label: 'Risk' },
  { key: 'cost', label: 'Cost' },
  { key: 'timeline', label: 'Timeline' },
] as const;

function metricValue(o: SpnDiscoveryOpportunity, key: (typeof METRICS)[number]['key']) {
  const dim = o.cases?.[key];
  if (!dim) return null;
  const level = o.chosen?.[key] ?? 'medium';
  // The five dimensions differ in their value type — timeline carries a number, the rest
  // strings — so the union has to be widened before indexing by level. Widened to the
  // shared shape rather than to `any`: what is read is still only `value`.
  const cases = dim as unknown as Record<string, { value?: string | number | null } | undefined>;
  const raw = cases[level]?.value;
  if (raw == null) return null;
  return { text: key === 'timeline' ? `${raw} wk` : String(raw), tone: dim.tone ?? 'moderate' };
}

export function Transfer() {
  const [ready, setReady] = useState<SpnDiscoveryOpportunity[]>([]);
  const [transferred, setTransferred] = useState<SpnDiscoveryOpportunity[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [flash, setFlash] = useState<string>();
  /**
   * Which rows are open. Collapsed by default, and tracked as what is OPEN rather than
   * what is shut — the same shape as the session screen, so the two behave alike.
   */
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  /**
   * Business areas folded shut, by name. Empty means all open — the same shape and the same
   * default as the Discoveries screen, so the two lists behave identically.
   */
  const [closedAreas, setClosedAreas] = useState<Set<string>>(new Set());

  /** Group by business area, in the order the server returned. */
  const byArea = useCallback((rows: SpnDiscoveryOpportunity[]) => {
    const out: Array<{ area: string; items: SpnDiscoveryOpportunity[] }> = [];
    for (const o of rows) {
      const last = out[out.length - 1];
      if (last && last.area === o.business_area) last.items.push(o);
      else out.push({ area: o.business_area, items: [o] });
    }
    return out;
  }, []);

  const toggleRow = useCallback(
    (id: number) =>
      setExpanded((prev) => {
        const next = new Set(prev);
        if (!next.delete(id)) next.add(id);
        return next;
      }),
    [],
  );

  const toggleArea = useCallback(
    (key: string) =>
      setClosedAreas((prev) => {
        const next = new Set(prev);
        if (!next.delete(key)) next.add(key);
        return next;
      }),
    [],
  );

  const toggleMany = useCallback(
    (rows: SpnDiscoveryOpportunity[]) =>
      setExpanded((prev) => {
        const next = new Set(prev);
        const allOpen = rows.every((o) => next.has(o.id));
        rows.forEach((o) => (allOpen ? next.delete(o.id) : next.add(o.id)));
        return next;
      }),
    [],
  );

  const load = useCallback(async () => {
    const board = await api.spn.getTransferBoard();
    setReady(board.ready);
    setTransferred(board.transferred);
    // Anything that has left is no longer pickable, so it must not linger in the selection.
    setPicked((prev) => new Set([...prev].filter((id) => board.ready.some((o) => o.id === id))));
  }, []);

  useEffect(() => {
    let alive = true;
    load()
      .catch((e) => {
        if (alive) setError((e as Error).message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [load]);

  const pickedValue = useMemo(
    () => ready.filter((o) => picked.has(o.id)).reduce((a, o) => a + chosenValue(o), 0),
    [ready, picked],
  );
  const transferredValue = useMemo(
    () => transferred.reduce((a, o) => a + chosenValue(o), 0),
    [transferred],
  );

  /** No session code — this screen spans every discovery, and so do its documents. */
  const exportAs = async (format: 'docx' | 'pptx') => {
    setBusy(true);
    setError(undefined);
    try {
      await api.spn.downloadExport(format);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const ids = [...picked];
    if (!ids.length) return;
    if (
      !window.confirm(
        `Transfer ${ids.length} opportunit${ids.length === 1 ? 'y' : 'ies'} to Portfolio ` +
          'Definition? They become read-only here until they are deleted there.',
      )
    )
      return;

    setBusy(true);
    setError(undefined);
    setFlash(undefined);
    try {
      const result = await api.spn.transferOpportunities(ids);
      await load();
      setPicked(new Set());
      // Both halves reported. A partial transfer that only says how many succeeded leaves
      // the client to work out what did not, which is the thing they need to know.
      setFlash(
        `${result.transferred.length} transferred` +
          (result.refused.length
            ? ` · ${result.refused.length} refused: ${result.refused
                .map((r) => `#${r.id} ${r.reason}`)
                .join('; ')}`
            : ''),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="note">Loading…</p>;

  const allPicked = ready.length > 0 && picked.size === ready.length;

  return (
    <div className="ds tx">
      <HelpPanel help={HELP} />
      <PageHeader
        title="Transfer"
        description="Hand settled opportunities to Opportunity Registry. Transferred opportunities are locked here."
      />

      {error ? <p style={{ color: '#b91c1c', fontSize: 12.5 }}>{error}</p> : null}
      {flash ? <p style={{ color: '#1f6b38', fontSize: 12.5 }}>{flash}</p> : null}

      <div className="ds-stats">
        <div className="ds-stat lead">
          <div className="k">Ready to transfer</div>
          <div className="v">{ready.length}</div>
          <div className="s">ticked inside a discovery</div>
        </div>
        <div className="ds-stat">
          <div className="k">Selected here</div>
          <div className="v">{picked.size}</div>
          <div className="s num">{compact(pickedValue)} a year</div>
        </div>
        <div className="ds-stat">
          <div className="k">Transferred</div>
          <div className="v">{transferred.length}</div>
          <div className="s">locked here</div>
        </div>
        <div className="ds-stat">
          <div className="k">Value transferred</div>
          <div className="v num">{compact(transferredValue)}</div>
          <div className="s">now in Opportunity Registry</div>
        </div>
      </div>

      <div className="ds-card ds-bar">
        {/* Exports everything TICKED across every discovery — this screen is where the whole
            handover is assembled, so its documents are the whole handover too. */}
        <button
          className="ds-rd-btn word"
          onClick={() => exportAs('docx')}
          disabled={busy || !ready.length}
          title={ready.length ? 'Word document for everything ready' : 'Nothing to export'}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3.5 1.5h6L13 5v9.5H3.5z" />
            <path d="M9.5 1.5V5H13" />
            <path d="M5.5 8.5l1 3 1.5-3 1.5 3 1-3" />
          </svg>
          Word
        </button>
        <button
          className="ds-rd-btn ppt"
          onClick={() => exportAs('pptx')}
          disabled={busy || !ready.length}
          title={ready.length ? 'PowerPoint deck for everything ready' : 'Nothing to export'}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="2.5" width="12" height="8.5" rx="1" />
            <path d="M8 11v3M5.5 14h5" />
            <circle cx="8" cy="6.75" r="2" />
          </svg>
          PowerPoint
        </button>
        <span style={{ width: 8 }} />
        <button className="ds-btn" onClick={send} disabled={busy || !picked.size}>
          {busy
            ? 'Transferring…'
            : picked.size
              ? `Transfer ${picked.size} opportunit${picked.size === 1 ? 'y' : 'ies'}`
              : 'Transfer'}
        </button>
        <span className="note">
          {picked.size
            ? 'Locks them here. Opportunity Registry takes over.'
            : 'Tick what you are sending.'}
        </span>
        <span className="spacer" />
        <button
          className="ds-btn quiet sm"
          onClick={() => setPicked(new Set())}
          disabled={!picked.size}
        >
          Clear selection
        </button>
      </div>

      {/* ── ready ──────────────────────────────────────────────────────── */}
      <div className="ds-card tx-sec">
        <div className="tx-head">
          <input
            type="checkbox"
            checked={allPicked}
            ref={(el) => {
              if (el) el.indeterminate = picked.size > 0 && !allPicked;
            }}
            onChange={(e) => setPicked(e.target.checked ? new Set(ready.map((o) => o.id)) : new Set())}
            disabled={!ready.length}
          />
          <div>
            <div className="nm">Ready to transfer</div>
            <div className="ct">Ticked inside a discovery, and still editable there</div>
          </div>
          <span className="spacer" />
          <span className="note">
            {picked.size} of {ready.length} selected
          </span>
          <button
            className="ds-btn quiet sm"
            onClick={() => toggleMany(ready)}
            disabled={!ready.length}
          >
            {ready.length && ready.every((o) => expanded.has(o.id)) ? 'Collapse' : 'Expand'} all
          </button>
        </div>

        {ready.length === 0 ? (
          <p className="tx-empty">
            Nothing ready. Open a discovery and tick the opportunities you are carrying.
          </p>
        ) : (
          byArea(ready).map((group) => (
            <AreaBlock
              key={group.area}
              group={group}
              open={!closedAreas.has(group.area)}
              onToggle={() => toggleArea(group.area)}
              expanded={expanded}
              onToggleAll={() => toggleMany(group.items)}
              picked={picked}
              onPickArea={(on) =>
                setPicked((prev) => {
                  const next = new Set(prev);
                  group.items.forEach((o) => (on ? next.add(o.id) : next.delete(o.id)));
                  return next;
                })
              }
              renderRow={(o) => (
                <Row
                  key={o.id}
                  o={o}
                  expanded={expanded.has(o.id)}
                  onToggleExpanded={() => toggleRow(o.id)}
                  picked={picked.has(o.id)}
                  onPick={(v) =>
                    setPicked((prev) => {
                      const next = new Set(prev);
                      if (v) next.add(o.id);
                      else next.delete(o.id);
                      return next;
                    })
                  }
                />
              )}
            />
          ))
        )}
      </div>

      {/* ── transferred ────────────────────────────────────────────────── */}
      <div className="ds-card tx-sec">
        <div className="tx-head locked">
          <span style={{ width: 15 }} />
          <div>
            <div className="nm">Transferred · locked</div>
            <div className="ct">
              Read-only here. Deleting one in Opportunity Registry releases it back above.
            </div>
          </div>
          <span className="spacer" />
          <button
            className="ds-btn quiet sm"
            onClick={() => toggleMany(transferred)}
            disabled={!transferred.length}
          >
            {transferred.length && transferred.every((o) => expanded.has(o.id))
              ? 'Collapse'
              : 'Expand'}{' '}
            all
          </button>
        </div>

        {transferred.length === 0 ? (
          <p className="tx-empty">Nothing transferred yet.</p>
        ) : (
          byArea(transferred).map((group) => (
            <AreaBlock
              key={group.area}
              group={group}
              open={!closedAreas.has(`locked:${group.area}`)}
              onToggle={() => toggleArea(`locked:${group.area}`)}
              expanded={expanded}
              onToggleAll={() => toggleMany(group.items)}
              renderRow={(o) => (
                <Row
                  key={o.id}
                  o={o}
                  locked
                  expanded={expanded.has(o.id)}
                  onToggleExpanded={() => toggleRow(o.id)}
                />
              )}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * One business area and its opportunities.
 *
 * THE FIRST NODE, as on Discoveries — the same caret, the same heading treatment, the same
 * Expand-all. The two screens list the same things and had no business grouping them
 * differently.
 *
 * `onPickArea` is absent on the transferred list: those rows are locked, so a tick-all
 * would offer an action that every row beneath it refuses.
 */
function AreaBlock({
  group,
  open,
  onToggle,
  expanded,
  onToggleAll,
  picked,
  onPickArea,
  renderRow,
}: {
  group: { area: string; items: SpnDiscoveryOpportunity[] };
  open: boolean;
  onToggle: () => void;
  expanded: Set<number>;
  onToggleAll: () => void;
  picked?: Set<number>;
  onPickArea?: (on: boolean) => void;
  renderRow: (o: SpnDiscoveryOpportunity) => React.ReactNode;
}) {
  const pickedHere = picked ? group.items.filter((o) => picked.has(o.id)).length : 0;
  const allOpen = group.items.every((o) => expanded.has(o.id));
  const value = group.items.reduce((a, o) => a + chosenValue(o), 0);

  return (
    <div>
      <div className="tx-area">
        {onPickArea ? (
          <input
            type="checkbox"
            checked={pickedHere === group.items.length && pickedHere > 0}
            ref={(el) => {
              if (el) el.indeterminate = pickedHere > 0 && pickedHere < group.items.length;
            }}
            onChange={(e) => onPickArea(e.target.checked)}
          />
        ) : (
          <span style={{ width: 15 }} />
        )}

        <button
          type="button"
          className="ds-caret"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${group.area}`}
          title={`${open ? 'Collapse' : 'Expand'} ${group.area}`}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? <path d="M3.5 6l4.5 4.5L12.5 6" /> : <path d="M6 3.5L10.5 8 6 12.5" />}
          </svg>
        </button>

        <div>
          <div className="nm">{group.area}</div>
          <div className="ct">
            {group.items.length} opportunit{group.items.length === 1 ? 'y' : 'ies'} ·{' '}
            {compact(value)}
          </div>
        </div>

        <span className="spacer" />
        <button className="ds-btn quiet sm" onClick={onToggleAll}>
          {allOpen ? 'Collapse' : 'Expand'} all
        </button>
      </div>

      {open
        ? group.items
            // A revision shows only when its original is expanded — the same rule as the
            // session screen.
            .filter((o) => !o.variant_of_id || expanded.has(o.variant_of_id))
            .map((o) => renderRow(o))
        : null}
    </div>
  );
}

function Row({
  o,
  picked,
  onPick,
  locked,
  expanded,
  onToggleExpanded,
}: {
  o: SpnDiscoveryOpportunity;
  picked?: boolean;
  onPick?: (v: boolean) => void;
  locked?: boolean;
  /** Collapsed by default, exactly as on the session screen. */
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  return (
    <div
      className={
        `tx-row${locked ? ' lock' : ''}` +
        `${o.variant_of_id ? ' variant' : ''}` +
        `${o.variant_of_id || o.variant_label ? ' grouped' : ''}`
      }
    >
      {locked ? (
        <span className="tx-lockicon" aria-hidden>
          🔒
        </span>
      ) : (
        <input type="checkbox" checked={Boolean(picked)} onChange={(e) => onPick?.(e.target.checked)} />
      )}

      {/* The same 24px caret the session screen and the business area use. Its own column,
          so titles line up whether a row is open or shut. A revision has none — it is
          inside the group its original opens. */}
      {o.variant_of_id ? (
        <span aria-hidden />
      ) : (
        <button
          type="button"
          className="ds-caret"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${o.description}`}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            {expanded ? <path d="M3.5 6l4.5 4.5L12.5 6" /> : <path d="M6 3.5L10.5 8 6 12.5" />}
          </svg>
        </button>
      )}

      <div style={{ minWidth: 0 }}>
        <div className="d">
          {o.description}
          {o.variant_label ? <span className="ds-variant-tag">{o.variant_label}</span> : null}
        </div>
        <div className="u">
          {o.business_unit || 'the company as a whole'} · {o.business_area}
        </div>

        {!expanded ? null : locked ? (
          <div className="tx-lockline">
            <span className="tx-lockchip">🔒 Locked</span>
            <span className="note">
              Opportunity Registry
              {o.transfer_ref ? ` · ${o.transfer_ref}` : ''}
              {o.transferred_at ? ` · ${new Date(o.transferred_at).toLocaleString()}` : ''}
            </span>
          </div>
        ) : (
          <div className="tx-metrics">
            {METRICS.map((m) => {
              const v = metricValue(o, m.key);
              if (!v) return null;
              return (
                <span className="tx-m" key={m.key}>
                  {m.label}
                  <b className={v.tone}>{v.text}</b>
                </span>
              );
            })}
            {o.released_at ? (
              <span className="tx-returned">returned {new Date(o.released_at).toLocaleDateString()}</span>
            ) : null}
          </div>
        )}
      </div>

      <div className="v">
        <b className="num">{compact(chosenValue(o))}</b>
        <div>{locked ? 'as transferred' : `a year, ${o.chosen?.benefit ?? 'medium'}`}</div>
      </div>
    </div>
  );
}
